import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAppStore from '../store/useAppStore';
import { questions } from '../data/questions';
import { ALL_BADGES } from '../data/badges';
import { stages } from '../data/stages';
import RadarChart from '../components/RadarChart';
import { Module, Stage, BadgeCategory } from '../types';
import styles from './Dashboard.module.css';

// 直接在组件内计算模块掌握度，避免 getter 返回新数组导致 Zustand 无限重渲染
const computeModuleMastery = (
  completedLevels: { levelId: string; correctCount: number; totalCount: number; timestamp: number }[]
) => {
  const allModules = [Module.FEEDING, Module.SLEEP, Module.DAILY_CARE, Module.SAFETY, Module.DEVELOPMENT];
  const totalPerModule: Record<string, number> = {
    [Module.FEEDING]: 3,
    [Module.SLEEP]: 3,
    [Module.DAILY_CARE]: 3,
    [Module.SAFETY]: 3,
    [Module.DEVELOPMENT]: 2,
  };
  return allModules.map((mod) => {
    const modulePrefix =
      mod === Module.FEEDING ? 'feeding'
      : mod === Module.SLEEP ? 'sleep'
      : mod === Module.DAILY_CARE ? 'care'
      : mod === Module.SAFETY ? 'safety'
      : 'dev';
    const moduleRecords = completedLevels.filter((l) => l.levelId.includes(modulePrefix));
    const levelLatest: Record<string, typeof moduleRecords[number]> = {};
    moduleRecords.forEach((l) => {
      if (!levelLatest[l.levelId] || l.timestamp > levelLatest[l.levelId].timestamp) {
        levelLatest[l.levelId] = l;
      }
    });
    const records = Object.values(levelLatest);
    const totalCorrect = records.reduce((sum, l) => sum + l.correctCount, 0);
    const totalQ = records.reduce((sum, l) => sum + l.totalCount, 0);
    return {
      module: mod,
      accuracy: totalQ > 0 ? Math.round((totalCorrect / totalQ) * 100) : 0,
      completedLevels: records.length,
      totalLevels: totalPerModule[mod] || 0,
    };
  });
};

type TabKey = 'status' | 'achievements';

const categoryMeta: Record<BadgeCategory, { icon: string; color: string }> = {
  [BadgeCategory.LEARNING]: { icon: '📖', color: '#5a9e6f' },
  [BadgeCategory.PERSISTENCE]: { icon: '🔥', color: '#e8734a' },
  [BadgeCategory.CHALLENGE]: { icon: '⚔️', color: '#7b68ee' },
  [BadgeCategory.REVIEW]: { icon: '📝', color: '#e8a84a' },
  [BadgeCategory.EXPLORATION]: { icon: '🧭', color: '#4a9e8e' },
  [BadgeCategory.SPECIAL]: { icon: '🎲', color: '#c96a9e' },
  [BadgeCategory.HIDDEN]: { icon: '❓', color: '#8b7b8b' },
};

const stageIcons: Record<string, string> = {
  [Stage.NEWBORN]: '🏡',
  [Stage.ONE_TO_THREE]: '🌿',
  [Stage.FOUR_TO_SIX]: '🌸',
  [Stage.SEVEN_TO_TWELVE]: '🏃',
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('status');

  // ===== 实时状态用到的数据 =====
  const completedLevels = useAppStore((s) => s.completedLevels);
  const wrongQuestions = useAppStore((s) => s.wrongQuestions);
  const reviewCount = useAppStore((s) => s.reviewCount);
  const accuracy = useAppStore((s) => s.getAccuracy());
  const stageRate = useAppStore((s) => s.getStageCompletionRate());
  const wrongCount = useAppStore((s) => s.getWrongCount());
  const streak = useAppStore((s) => s.streak);
  const totalStars = useAppStore((s) => s.getTotalStars());
  const currentTitle = useAppStore((s) => s.getCurrentTitle());

  // ===== 里程碑成就用到的数据 =====
  const userRole = useAppStore((s) => s.userRole);
  const babyBorn = useAppStore((s) => s.babyBorn);
  const babyDate = useAppStore((s) => s.babyDate);
  const dueDate = useAppStore((s) => s.dueDate);
  const onboardingComplete = useAppStore((s) => s.onboardingComplete);
  const stageChallengeRecords = useAppStore((s) => s.stageChallengeRecords);
  const bonusStars = useAppStore((s) => s.bonusStars);
  const dailyFirstWinDate = useAppStore((s) => s.dailyFirstWinDate);
  const badgeAuxData = useAppStore((s) => s.badgeAuxData);

  // 在 useMemo 中计算模块掌握度，依赖 completedLevels 的实际变化
  const moduleMastery = useMemo(() => computeModuleMastery(completedLevels), [completedLevels]);

  const totalCorrect = completedLevels.reduce((sum, l) => sum + l.correctCount, 0);
  const totalQuestions = completedLevels.reduce((sum, l) => sum + l.totalCount, 0);

  // 薄弱模块分析
  const weakModules = wrongQuestions.reduce<string[]>((acc, w) => {
    const q = questions.find((q) => q.id === w.questionId);
    if (q && !w.retryCorrect) {
      const module = q.levelId.includes('feeding')
        ? '喂养'
        : q.levelId.includes('sleep')
        ? '睡眠'
        : '其他';
      if (!acc.includes(module)) acc.push(module);
    }
    return acc;
  }, []);

  // ===== 连胜里程碑进度 =====
  const nextMilestone = useMemo(() => {
    const milestones = [3, 7, 14, 30];
    for (const m of milestones) {
      if (streak.currentStreak < m) return m;
    }
    return null;
  }, [streak.currentStreak]);

  const milestoneProgress = nextMilestone
    ? Math.round((streak.currentStreak / nextMilestone) * 100)
    : 100;

  // ===== 徽章解锁状态（精简化：只计算总数 + 已解锁列表） =====
  const badgeSnapshot = useMemo(() => ({
    completedLevels: completedLevels.map((l) => ({ ...l })),
    wrongQuestions: wrongQuestions.map((w) => ({ ...w })),
    reviewCount,
    streak: { ...streak },
    stageChallengeRecords: { ...stageChallengeRecords },
    bonusStars,
    dailyFirstWinDate,
    userRole: userRole as string | null,
    babyBorn,
    babyDate,
    dueDate,
    onboardingComplete,
    ...badgeAuxData,
  }), [completedLevels, wrongQuestions, reviewCount, streak, stageChallengeRecords, bonusStars, dailyFirstWinDate, userRole, babyBorn, babyDate, dueDate, onboardingComplete, badgeAuxData]);

  const { unlockedBadges, unlockedCount, totalCount } = useMemo(() => {
    const unlocked = ALL_BADGES.filter((b) => b.unlocked(badgeSnapshot));
    return {
      unlockedBadges: unlocked,
      unlockedCount: unlocked.length,
      totalCount: ALL_BADGES.length,
    };
  }, [badgeSnapshot]);

  // ===== 阶段完成状态 =====
  const completedStages = badgeAuxData.stageCompleted || [];

  // ===== 渲染实时状态 Tab =====
  const renderStatusTab = () => (
    <>
      {/* 核心指标 */}
      <div className={styles.metrics}>
        <div className={styles.metricCard}>
          <div className={styles.metricValue}>{stageRate}%</div>
          <div className={styles.metricLabel}>阶段完成率</div>
        </div>
        <div className={styles.metricCard}>
          <div className={styles.metricValue}>{accuracy}%</div>
          <div className={styles.metricLabel}>总正确率</div>
        </div>
        <div className={styles.metricCard}>
          <div className={`${styles.metricValue} ${wrongCount > 0 ? styles.warn : ''}`}>
            {wrongCount}
          </div>
          <div className={styles.metricLabel}>待复习错题</div>
        </div>
        <div className={styles.metricCard}>
          <div className={styles.metricValue}>{reviewCount}</div>
          <div className={styles.metricLabel}>复习次数</div>
        </div>
      </div>

      {/* 连胜 + 称号 */}
      {(streak.currentStreak > 0 || totalStars > 0) && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>学习成就</h2>
          <div className={styles.achievementRow}>
            {streak.currentStreak > 0 && (
              <div className={styles.achievementItem}>
                <span className={styles.achievementIcon}>🔥</span>
                <div>
                  <div className={styles.achievementValue}>当前连胜 <strong>{streak.currentStreak}</strong> 天</div>
                  <div className={styles.achievementSub}>最长连胜 {streak.longestStreak} 天</div>
                </div>
              </div>
            )}
            <div className={styles.achievementItem}>
              <span className={styles.achievementIcon}>⭐</span>
              <div>
                <div className={styles.achievementValue}>{currentTitle}</div>
                <div className={styles.achievementSub}>累计 {totalStars} 颗星</div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 模块雷达图 */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>模块掌握度</h2>
        <div className={styles.radarWrap}>
          <RadarChart
            data={moduleMastery.map((m) => ({
              label: m.module,
              value: m.accuracy,
            }))}
            size={240}
          />
        </div>
        <div className={styles.moduleDetail}>
          {moduleMastery.map((m) => (
            <div key={m.module} className={styles.moduleDetailItem}>
              <span className={styles.moduleDetailName}>{m.module}</span>
              <span className={styles.moduleDetailBar}>
                <span
                  className={styles.moduleDetailFill}
                  style={{ width: `${m.accuracy}%` }}
                />
              </span>
              <span className={styles.moduleDetailVal}>
                {m.completedLevels}/{m.totalLevels}关 · {m.accuracy}%
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* 统计详情 */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>学习统计</h2>
        <div className={styles.statList}>
          <div className={styles.statItem}>
            <span>已完成关卡</span>
            <span className={styles.statVal}>{completedLevels.length}</span>
          </div>
          <div className={styles.statItem}>
            <span>答题总数</span>
            <span className={styles.statVal}>{totalQuestions}</span>
          </div>
          <div className={styles.statItem}>
            <span>答对次数</span>
            <span className={styles.statVal}>{totalCorrect}</span>
          </div>
          <div className={styles.statItem}>
            <span>答题正确率</span>
            <span className={styles.statVal}>{accuracy}%</span>
          </div>
        </div>
      </section>

      {/* 薄弱模块 */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>建议复习</h2>
        {weakModules.length > 0 ? (
          <div className={styles.weakList}>
            {weakModules.map((m) => (
              <div key={m} className={styles.weakTag}>
                {m}
              </div>
            ))}
          </div>
        ) : (
          <p className={styles.noWeak}>暂无薄弱模块，继续保持！</p>
        )}
        <button className={styles.gotoReview} onClick={() => navigate('/review')}>
          进入复习中心 →
        </button>
      </section>

      {/* 最近学习记录 */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>最近学习</h2>
        {completedLevels.length > 0 ? (
          <div className={styles.historyList}>
            {[...completedLevels].reverse().slice(0, 5).map((l, idx) => (
              <div key={idx} className={styles.historyItem}>
                <div className={styles.historyDot} />
                <div>
                  <div className={styles.historyName}>完成关卡 {l.levelId}</div>
                  <div className={styles.historyTime}>
                    {new Date(l.timestamp).toLocaleString()} · {l.score}/{l.totalCount} 分
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className={styles.noWeak}>还没有学习记录，去开始第一关吧！</p>
        )}
      </section>
    </>
  );

  // ===== 渲染里程碑成就 Tab（精简化） =====
  const renderAchievementsTab = () => (
    <>
      {/* 连胜里程碑进度 */}
      {streak.currentStreak > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>连胜里程碑</h2>
          <div className={styles.milestoneBar}>
            <div className={styles.milestoneNodes}>
              {[3, 7, 14, 30].map((m) => {
                const reached = streak.currentStreak >= m;
                return (
                  <div
                    key={m}
                    className={`${styles.milestoneNode} ${reached ? styles.milestoneReached : ''}`}
                  >
                    <span className={styles.milestoneNodeIcon}>
                      {reached ? '✅' : '🔒'}
                    </span>
                    <span className={styles.milestoneNodeDay}>{m}天</span>
                  </div>
                );
              })}
            </div>
            <div className={styles.milestoneProgressWrap}>
              <div className={styles.milestoneProgressBar}>
                <div
                  className={styles.milestoneProgressFill}
                  style={{ width: `${milestoneProgress}%` }}
                />
              </div>
              <span className={styles.milestoneProgressLabel}>
                {nextMilestone
                  ? `距 ${nextMilestone} 天里程碑还差 ${nextMilestone - streak.currentStreak} 天`
                  : '🏆 已解锁全部里程碑！'}
              </span>
            </div>
          </div>
        </section>
      )}

      {/* 阶段完成徽章 */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>阶段完成</h2>
        {stages.length > 0 ? (
          <div className={styles.stageBadgeRow}>
            {stages.map((s) => {
              const done = completedStages.includes(s.name);
              return (
                <div
                  key={s.id}
                  className={`${styles.stageBadge} ${done ? styles.stageBadgeDone : styles.stageBadgePending}`}
                >
                  <span className={styles.stageBadgeIcon}>
                    {done ? stageIcons[s.name] || '🌟' : '🔒'}
                  </span>
                  <span className={styles.stageBadgeName}>{s.name}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className={styles.noWeak}>暂无阶段数据</p>
        )}
      </section>

      {/* 成就徽章概览 */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>成就徽章概览</h2>
        <div className={styles.badgeOverview}>
          <div className={styles.badgeOverviewHeader}>
            <span className={styles.badgeOverviewCount}>
              已解锁 <strong>{unlockedCount}</strong> / {totalCount}
            </span>
            <span className={styles.badgeOverviewPct}>
              {Math.round((unlockedCount / totalCount) * 100)}%
            </span>
          </div>
          <div className={styles.badgeOverviewBar}>
            <div
              className={styles.badgeOverviewFill}
              style={{ width: `${(unlockedCount / totalCount) * 100}%` }}
            />
          </div>
        </div>

        {/* 精简化徽章网格：只展示已解锁的，最多 6 个，按分类分散 */}
        {unlockedBadges.length > 0 ? (
          <div className={styles.compactBadgeGrid}>
            {unlockedBadges.slice(0, 6).map((badge) => {
              const meta = categoryMeta[badge.category];
              return (
                <div
                  key={badge.id}
                  className={styles.compactBadgeCard}
                  style={{ borderColor: meta.color }}
                >
                  <span className={styles.compactBadgeIcon}>{badge.icon}</span>
                  <span className={styles.compactBadgeName}>{badge.name}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className={styles.noWeak}>还没有解锁任何徽章，开始学习吧！</p>
        )}

        <button
          className={styles.gotoFullAchievements}
          onClick={() => navigate('/achievements')}
        >
          查看全部 {totalCount} 个成就 →
        </button>
      </section>
    </>
  );

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate('/')}>
          ← 返回首页
        </button>
        <h1 className={styles.title}>实时状态</h1>
        <p className={styles.subtitle}>持续学习，每一步都在进步</p>

        {/* Tab 切换 */}
        <div className={styles.tabBar}>
          <button
            className={`${styles.tabBtn} ${activeTab === 'status' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('status')}
          >
            📊 掌握情况
          </button>
          <button
            className={`${styles.tabBtn} ${activeTab === 'achievements' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('achievements')}
          >
            🏆 里程碑成就
          </button>
        </div>
      </header>

      {activeTab === 'status' ? renderStatusTab() : renderAchievementsTab()}
    </div>
  );
};

export default Dashboard;